import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lock, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BRAND } from "@/lib/data";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/admin");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-foreground/20 py-3 text-foreground placeholder:text-muted-foreground/70 focus:border-accent focus:outline-none transition-colors duration-300";

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md"
      >
        <div className="flex items-center gap-3 mb-10">
          <span className="w-11 h-11 bg-foreground text-white flex items-center justify-center">
            <Lock size={18} />
          </span>
          <div>
            <div className="font-display text-3xl leading-none tracking-tight">{BRAND.name}</div>
            <div className="font-mono-label text-[0.6rem] uppercase tracking-widest text-accent mt-1">
              Admin Panel
            </div>
          </div>
        </div>

        <div className="bg-white border border-border p-8 sm:p-10 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.08)]">
          <h1 className="font-display text-4xl tracking-tight mb-8">Sign in</h1>
          <form onSubmit={submit} className="space-y-7" data-testid="admin-login-form">
            <input
              className={inputCls}
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email"
            />
            <input
              className={inputCls}
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password"
            />
            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-3 bg-foreground text-white py-4 text-xs font-mono-label uppercase tracking-widest hover:bg-accent transition-colors duration-300 disabled:opacity-60"
              data-testid="login-submit"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
